import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import type { IssueDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import { IssueAssessmentStatus as Issue_Assessment_Status_Enum } from '@risksmart-app/domain/src/types/consts/issue-assessment-status';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import i18n from '@risksmart-app/i18n/src/i18n';
import _ from 'lodash';
import { useGetIssueRegister } from 'src/hooks/queries/issue/useGetIssueRegister';
import type { IssueRegisterFields } from 'src/pages/issues/types';

import type { DashboardFilterArgs } from '@/hooks/queries/dashboardFilterArgs';
import { IssueTypeMapping } from '@/utils/issueVariantUtils';

import { useGetIssueSmartWidgetTableProps } from '../../../issues/config';
import type { WidgetQueryParams } from '../../gigawidget/types';
import { UNRATED } from '../../gigawidget/types';
import { departmentGetter } from '../../gigawidget/util/categoryGetters';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';

type GetIssuesData = NonNullable<
  ReturnType<typeof useGetIssueRegister>['data']
>;

type IssueWidgetArgs = DashboardFilterArgs<IssueDashboardDateField>;

export default (issueType: ParentIssueType) =>
  createDataSource<IssueRegisterFields, IssueWidgetArgs, GetIssuesData>({
    group: 'single',
    parentTypes: [issueType, IssueTypeMapping[issueType].assessmentType],
    hasAccess: (_, __, isFeatureFlagEnabled) =>
      issueType === Parent_Type_Enum.Issue ||
      !!(
        IssueTypeMapping[issueType].featureFlag &&
        isFeatureFlagEnabled(IssueTypeMapping[issueType].featureFlag)
      ),
    useWidgetQuery: (params: WidgetQueryParams<IssueWidgetArgs>) =>
      useGetIssueRegister({
        ...params,
        queryArgs: { ...params.queryArgs, issueType },
      }),
    useTablePropsHook: (data, options) => {
      return useGetIssueSmartWidgetTableProps(issueType, data?.issue, options);
    },
    entityNamePlural: IssueTypeMapping[issueType].entityLabelOther,
    entityNameSingular: IssueTypeMapping[issueType].entityLabel,
    fields: `${IssueTypeMapping[issueType].taxonomy}.fields`,
    dashboardFilterConfig: {
      tagsFilter: (tags) => ({ tagTypeIds: tags }),
      departmentsFilter: (departments) => ({ departmentTypeIds: departments }),
      dateFilter: (dateRange, precision) => ({
        dateRange: {
          field: 'CreatedAtTimestamp',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      dateClickthroughFilter:
        dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
      ownershipFilter: (myItemsFilters) => ({ ownership: myItemsFilters }),
    },
    clickThroughUrl: (filter, sorter) =>
      IssueTypeMapping[issueType].registerUrl({
        filtering: filter,
        sorting: sorter,
      }),
    categoryGetters: [
      ...tagAndDepartmentCategoryGetters<IssueRegisterFields>(),
      {
        id: 'assessmentDepartments',
        name: () =>
          i18n.t(
            `${IssueTypeMapping[issueType].taxonomy}.columns.assessment_departments`
          ),
        categoryGetter: (item) =>
          item.assessment
            ? departmentGetter({ includeNoDepartments: true })(item.assessment)
            : null,
        clickthroughFilter: (category) => [
          {
            propertyKey: 'AssessmentDepartments',
            operator: category.key === UNRATED ? '<' : '=',
            value: category.key === UNRATED ? 1 : category.key,
          },
        ],
      },
      {
        id: 'status',
        name: () =>
          i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.status`),
        categoryGetter: (item) => ({
          key: item.StatusLabelled
            ? item.StatusLabelled.toLowerCase()
            : Issue_Assessment_Status_Enum.Pending,
          label: item.StatusLabelled ?? UNRATED,
        }),
        clickthroughFilter:
          defaultClickthroughFilterWithUnrated('StatusLabelled'),
        ratingColourKey: 'issue_assessment_status',
      },
      {
        id: 'owner',
        fieldId: 'Owners',
        name: () =>
          i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.owner`),
        categoryGetter: (item) =>
          item.allOwners.map((owner) => ({
            key: owner.id,
            label: owner.label,
          })),
        clickthroughFilter: (category) => [
          {
            propertyKey: 'allOwners',
            operator: '=',
            value: category.key,
          },
        ],
      },
      {
        id: 'contributor',
        fieldId: 'Contributors',
        name: () =>
          i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.contributor`),
        categoryGetter: (item) =>
          item.allContributors.map((contributor) => ({
            key: contributor.id,
            label: contributor.label,
          })),
        clickthroughFilter: (category) => [
          {
            propertyKey: 'allContributors',
            operator: '=',
            value: category.key,
          },
        ],
      },
      {
        id: 'impactsCustomers',
        fieldId: 'ImpactsCustomer',
        name: () =>
          i18n.t(
            `${IssueTypeMapping[issueType].taxonomy}.columns.impacts_customer`
          ) + ' (Yes/No)',
        categoryGetter: (item) => ({
          key: String(item.ImpactsCustomer),
          label: _.isNil(item.ImpactsCustomer)
            ? 'Unspecified'
            : item.ImpactsCustomer
              ? 'Yes'
              : 'No',
        }),
        clickthroughFilter: defaultClickthroughFilterWithUnrated(
          'ImpactsCustomer',
          {
            categoryValue: 'key',
          }
        ),
      },
      {
        id: 'internalExternal',
        fieldId: 'IsExternalIssue',
        name: () =>
          i18n.t(
            `${IssueTypeMapping[issueType].taxonomy}.columns.is_external_issue`
          ) + ' (Yes/No)',
        categoryGetter: (item) => ({
          key: String(item.IsExternalIssue),
          label: _.isNil(item.IsExternalIssue)
            ? 'Unspecified'
            : item.IsExternalIssue
              ? 'External'
              : 'Internal',
        }),
        clickthroughFilter: defaultClickthroughFilterWithUnrated(
          'IsExternalIssue',
          {
            categoryValue: 'key',
          }
        ),
      },
      {
        id: 'severity',
        fieldId: 'Severity',
        name: () =>
          i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.severity`),
        categoryGetter: (item) => ({
          key: item.Severity ?? UNRATED,
          label: item.SeverityLabelled ?? UNRATED,
          sortKey: (item.Severity ?? -1).toString().padStart(2, '0'),
        }),
        ratingColourKey: 'severity',
        clickthroughFilter: defaultClickthroughFilterWithUnrated(
          'SeverityLabelled',
          { unratedValue: UNRATED }
        ),
      },
      {
        id: 'type',
        fieldId: 'IssueType',
        name: () =>
          i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.type`),
        categoryGetter: (item) => ({
          key: item.IssueType ?? UNRATED,
          label: item.IssueType ? item.IssueTypeLabelled : 'No Type',
        }),
        clickthroughFilter:
          defaultClickthroughFilterWithUnrated('IssueTypeLabelled'),
      },
      {
        id: 'dateIdentified',
        fieldId: 'DateIdentified',
        name: () =>
          i18n.t(
            `${IssueTypeMapping[issueType].taxonomy}.columns.date_identified`
          ),
        categoryGetter: (data) => new Date(data.DateIdentified),
        date: true,
        dashboardDateFilterOverride: (dateRange, precision) => ({
          dateRange: {
            field: 'DateIdentified',
            ...toIsoBounds(dateRange, precision),
          },
        }),
        clickthroughFilter: dateRangeClickthroughFilter('DateIdentified'),
      },
      {
        id: 'dateOccurred',
        fieldId: 'DateOccurred',
        name: () =>
          i18n.t(
            `${IssueTypeMapping[issueType].taxonomy}.columns.date_occurred`
          ),
        categoryGetter: (data) => new Date(data.DateOccurred),
        date: true,
        dashboardDateFilterOverride: (dateRange, precision) => ({
          dateRange: {
            field: 'DateOccurred',
            ...toIsoBounds(dateRange, precision),
          },
        }),
        clickthroughFilter: dateRangeClickthroughFilter('DateOccurred'),
      },
      {
        id: 'createdDate',
        name: () =>
          i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.createdOn`) +
          ' Date',
        categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
        date: true,
        dashboardDateFilterOverride: (dateRange, precision) => ({
          dateRange: {
            field: 'CreatedAtTimestamp',
            ...toIsoBounds(dateRange, precision),
          },
        }),
        clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
      },
      {
        id: 'dateRaised',
        name: () =>
          i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.raised`) +
          ' Date',
        categoryGetter: (data) => new Date(data.RaisedAtTimestamp),
        date: true,
        dashboardDateFilterOverride: (dateRange, precision) => ({
          dateRange: {
            field: 'RaisedAtTimestamp',
            ...toIsoBounds(dateRange, precision),
          },
        }),
        clickthroughFilter: dateRangeClickthroughFilter('RaisedAtTimestamp'),
      },
      {
        id: 'actualCloseDate',
        fieldId: 'ActualCloseDate',
        name: () =>
          i18n.t(
            `${IssueTypeMapping[issueType].taxonomy}.columns.actual_close_date`
          ),
        categoryGetter: (data) =>
          data.ActualCloseDate ? new Date(data.ActualCloseDate) : null,
        date: true,
        dashboardDateFilterOverride: (dateRange, precision) => ({
          dateRange: {
            field: 'ActualCloseDate',
            ...toIsoBounds(dateRange, precision),
          },
        }),
        clickthroughFilter: dateRangeClickthroughFilter('ActualCloseDate'),
      },
      {
        id: 'targetCloseDate',
        fieldId: 'TargetCloseDate',
        name: () =>
          `${i18n.t(`${IssueTypeMapping[issueType].taxonomy}.columns.target_close_date`)}`,
        categoryGetter: (data) =>
          data.TargetCloseDate ? new Date(data.TargetCloseDate) : null,
        date: true,
        dashboardDateFilterOverride: (dateRange, precision) => ({
          dateRange: {
            field: 'TargetCloseDate',
            ...toIsoBounds(dateRange, precision),
          },
        }),
        clickthroughFilter: dateRangeClickthroughFilter('TargetCloseDate'),
      },
    ],
  });
